import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import React from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  useDndMonitor,
  DragEndEvent,
  DragStartEvent,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import Skills from "../../../../types/Skills";
import Skill from "../../../../types/Skill";

type FieldValue = (string | Skill)[];
type SkillCategoryKey = keyof Skills;

interface CustomSkillsAutocompleteProps {
  setSkills: (e: any) => void;
  skills: Skills;
  error: string;
  removeErrorMessage: (field: string) => void;
  allSkills: Skill[];
  onCreateNewSkill: (name: string, type: Skill["type"]) => Promise<Skill>;
}

function skillToDragId(category: SkillCategoryKey, skillId: string): string {
  return `${category}-${skillId}`;
}

function DroppableCategory({
  id,
  children,
}: {
  id: SkillCategoryKey;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        margin: "1rem 0",
        borderRadius: 1,
        minHeight: 56,
        bgcolor: isOver ? "action.hover" : "transparent",
        transition: "background-color 0.2s",
      }}
    >
      {children}
    </Box>
  );
}

function DraggableSkillChip({
  skill,
  category,
  index,
  getTagProps,
  onDelete,
}: {
  skill: Skill;
  category: SkillCategoryKey;
  index: number;
  getTagProps: (params: { index: number }) => Record<string, unknown>;
  onDelete: () => void;
}) {
  const id = skillToDragId(category, skill.skillId);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { skill, sourceCategory: category },
  });
  return (
    <Chip
      ref={setNodeRef}
      label={skill.name}
      {...getTagProps({ index })}
      {...attributes}
      {...listeners}
      onDelete={onDelete}
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.3 : 1,
        transition: "opacity 0.2s ease",
      }}
    />
  );
}

const dragChipLift = keyframes`
  from { transform: scale(1); box-shadow: none; }
  to { transform: scale(1.08); box-shadow: 0 8px 16px rgba(0,0,0,0.15); }
`;

const dropAnimationConfig = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

function SkillsWithDragOverlay({
  children,
  error,
}: {
  children: React.ReactNode;
  error: string;
}) {
  const [activeDrag, setActiveDrag] = React.useState<{
    skill: Skill;
    sourceCategory: SkillCategoryKey;
  } | null>(null);

  useDndMonitor({
    onDragStart(event: DragStartEvent) {
      const data = event.active.data.current as { skill: Skill; sourceCategory: SkillCategoryKey } | undefined;
      if (data) setActiveDrag(data);
    },
    onDragEnd() {
      setActiveDrag(null);
    },
  });

  return (
    <>
      <DragOverlay dropAnimation={dropAnimationConfig}>
        {activeDrag ? (
          <Chip
            label={activeDrag.skill.name}
            sx={{
              cursor: "grabbing",
              animation: `${dragChipLift} 0.2s ease-out forwards`,
            }}
          />
        ) : null}
      </DragOverlay>
      <Box>
        {children}
        {error && (
          <Typography fontSize="12px" color="error" marginTop="3px">
            {error}
          </Typography>
        )}
      </Box>
    </>
  );
}

const CustomSkillsAutocomplete: React.FC<CustomSkillsAutocompleteProps> = ({
  skills,
  setSkills,
  error,
  removeErrorMessage,
  allSkills,
  onCreateNewSkill,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const data = active.data.current as { skill: Skill; sourceCategory: SkillCategoryKey } | undefined;
    if (!data) return;
    const targetCategory = over.id as SkillCategoryKey;
    const { skill, sourceCategory } = data;
    if (sourceCategory === targetCategory) return;

    const skillWithNewType: Skill = {
      ...skill,
      type: targetCategory === "others" ? "others" : targetCategory,
    };

    setSkills((prev: Skills) => {
      const sourceList = [...(prev[sourceCategory] || [])].filter(
        (s) => (typeof s === "string" ? s : s.skillId) !== skill.skillId
      );
      const targetList = prev[targetCategory] || [];
      const alreadyInTarget = targetList.some(
        (s) => (typeof s === "string" ? false : s.skillId === skill.skillId)
      );
      if (alreadyInTarget) return prev;
      return {
        ...prev,
        [sourceCategory]: sourceList,
        [targetCategory]: [...targetList, skillWithNewType],
      };
    });
    removeErrorMessage("skills");
  };

  const categorizedOptions = {
    backend: allSkills.filter((s) => s.type === "backend"),
    frontend: allSkills.filter((s) => s.type === "frontend"),
    devOps: allSkills.filter((s) => s.type === "devOps"),
    cloud: allSkills.filter((s) => s.type === "cloud"),
    database: allSkills.filter((s) => s.type === "database"),
    tools: allSkills.filter((s) => s.type === "tools"),
    others: allSkills.filter((s) => s.type === "others"),
  };

  const renderCategory = (label: string, category: SkillCategoryKey) => (
    <DroppableCategory key={category} id={category}>
      <Autocomplete
        sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
      multiple
      options={categorizedOptions[category].filter(
        (option) =>
          !skills[category].some(
            (s) => (typeof s === "string" ? s : s.name) === option.name
          )
      )}
      value={skills[category]}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      onKeyDown={(e) => {
        // Prevent parent form submit on Enter
        if (e.key === "Enter") e.stopPropagation();
      }}
      onChange={async (event, newValue: FieldValue) => {
        const inputStrings = newValue.filter(
          (v): v is string => typeof v === "string" && v.trim() !== ""
        );

        const existingSel = newValue.filter(
          (v): v is Skill => typeof v !== "string"
        );

        // Turn case-insensitive duplicates into canonical Skills (if already in definitions)
        const canonicalFromExistingDefs: Skill[] = inputStrings
          .map((name) =>
            allSkills.find(
              (s) =>
                s.type === category &&
                s.name.toLowerCase() === name.toLowerCase()
            )
          )
          .filter(Boolean) as Skill[];

        // Truly new strings (not in definitions yet)
        const trulyNew = inputStrings.filter(
          (name) =>
            !allSkills.some(
              (s) =>
                s.type === category &&
                s.name.toLowerCase() === name.toLowerCase()
            )
        );

        // Create all truly new skills on backend
        const created = await Promise.all(
          trulyNew.map((name) => onCreateNewSkill(name, category))
        );

        // Merge & dedupe by skillId
        const merged: Skill[] = [
          ...existingSel,
          ...canonicalFromExistingDefs,
          ...created,
        ];
        const unique = Array.from(
          new Map(merged.map((s) => [s.skillId, s])).values()
        );

        setSkills((prev: any) => ({ ...prev, [category]: unique }));
        removeErrorMessage("skills");
      }}
      freeSolo
      renderInput={(params) => (
        <TextField {...params} label={label} error={!!error} />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const isSkill = typeof option !== "string";
          const onDelete = () => {
            const newValue = value.filter((_, i) => i !== index);
            setSkills((prev: any) => ({ ...prev, [category]: newValue }));
            removeErrorMessage("skills");
          };
          if (isSkill) {
            return (
              <DraggableSkillChip
                key={(option as Skill).skillId}
                skill={option as Skill}
                category={category}
                index={index}
                getTagProps={getTagProps}
                onDelete={onDelete}
              />
            );
          }
          return (
            <Chip
              label={option as string}
              {...getTagProps({ index })}
              onDelete={onDelete}
            />
          );
        })
      }
    />
    </DroppableCategory>
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SkillsWithDragOverlay error={error}>
        {renderCategory("Backend", "backend")}
        {renderCategory("Frontend", "frontend")}
        {renderCategory("DevOps", "devOps")}
        {renderCategory("Cloud", "cloud")}
        {renderCategory("Database", "database")}
        {renderCategory("Tools", "tools")}
        {renderCategory("Others", "others")}
      </SkillsWithDragOverlay>
    </DndContext>
  );
};

export default CustomSkillsAutocomplete;
